import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Plus, Search, Filter, MoreVertical, 
  UserCheck, UserX, Edit, Trash2, Eye, CreditCard,
  Download, RefreshCw, MapPin, Phone, Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { HandHelping } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { formatDateIT } from '../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Opzioni ruoli associativi
const RUOLI_ASSOCIATIVI = [
  { value: '', label: 'Nessun ruolo' },
  { value: 'presidente', label: 'Presidente' },
  { value: 'vicepresidente', label: 'Vicepresidente' },
  { value: 'tesoriere', label: 'Tesoriere' },
  { value: 'segretario', label: 'Segretario' },
  { value: 'consigliere', label: 'Componente Consiglio Direttivo' },
  { value: 'revisore', label: 'Revisore dei Conti' },
  { value: 'volontario', label: 'Volontario' },
];

// Template vuoto per nuovo socio
const emptySocio = {
  codice_fiscale: '',
  nome: '',
  cognome: '',
  email: '',
  telefono: '',
  cellulare: '',
  cellulare_secondario: '',  // Secondo numero cellulare
  data_nascita: '',
  luogo_nascita: '',
  sesso: '',
  indirizzo: '',  // Indirizzo completo (legacy)
  via: '',  // Nome via (separato)
  numero_civico: '',  // Numero civico (separato)
  citta: '',
  cap: '',
  provincia: '',
  tipo_socio: 'ordinario',
  ruolo_associativo: '',
  tessera_associazione: '',  // Numero tessera rilasciato dall'associazione
  tessera_nazionale: '',  // Numero tessera federazione/ente nazionale
  familiare_collegato: '',  // Nome/riferimento familiare
  data_iscrizione_socio: '',  // Data iscrizione come socio
  note: '',
  crea_anche_volontario: false  // Ruolo aggiuntivo Volontario (Opzione B)
};

export default function Soci() {
  const navigate = useNavigate();
  const [soci, setSoci] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('tutti');
  const [filtroStato, setFiltroStato] = useState('attivo');
  
  // Dialog states
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({...emptySocio});
  const [editingPersonaId, setEditingPersonaId] = useState(null);
  const [editingHasVolontario, setEditingHasVolontario] = useState(false);
  const [viewingSocio, setViewingSocio] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 50;

  // Bulk selection (Idea Bonus)
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkReport, setBulkReport] = useState(null);

  const fetchSoci = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filtroTipo !== 'tutti') params.append('tipo_socio', filtroTipo);
      if (filtroStato !== 'tutti') params.append('stato', filtroStato);
      params.append('limit', '500');
      
      const response = await fetch(`${API_URL}/api/soci?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSoci(data.soci || []);
        setCurrentPage(1);
        // Reset selezione bulk quando cambia il filtro
        setSelectedIds(new Set());
        setBulkReport(null);
      }
    } catch (error) {
      console.error('Errore caricamento soci:', error);
      toast.error('Errore nel caricamento dei soci');
    }
  }, [search, filtroTipo, filtroStato]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/soci/stats/riepilogo`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Errore caricamento statistiche:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSoci(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchSoci, fetchStats]);

  const handleCreateSocio = async () => {
    // Validazione frontend più robusta
    if (!formData.codice_fiscale || formData.codice_fiscale.length !== 16) {
      toast.error('Il Codice Fiscale è obbligatorio e deve essere di 16 caratteri');
      return;
    }
    if (!formData.nome || formData.nome.trim() === '') {
      toast.error('Il Nome è obbligatorio');
      return;
    }
    if (!formData.cognome || formData.cognome.trim() === '') {
      toast.error('Il Cognome è obbligatorio');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      // Clean up empty fields and _none values
      const cleanData = {};
      for (const [key, value] of Object.entries(formData)) {
        if (value && value !== '' && value !== '_none') {
          cleanData[key] = value;
        }
      }
      
      const response = await fetch(`${API_URL}/api/soci`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(cleanData)
      });

      const responseClone = response.clone();
      
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        const text = await responseClone.text();
        console.error('JSON parse error:', parseError, 'Response text:', text);
        throw new Error('Risposta non valida dal server');
      }
      
      if (response.ok) {
        toast.success(`Socio ${data.nome_completo} creato con successo! Tessera: ${data.numero_tessera}`);
        if (data.volontario_created) {
          toast.success(`Ruolo Volontario aggiunto! Completa la scheda in modulo Volontari.`, { duration: 5000 });
        } else if (data.volontario_error) {
          toast.warning(`Socio creato, ma NON è stato possibile aggiungere il ruolo Volontario: ${data.volontario_error}`, { duration: 6000 });
        }
        setShowNewDialog(false);
        setFormData({...emptySocio});
        fetchSoci();
        fetchStats();
      } else {
        // Mostra errore specifico dal backend
        const errorMsg = data.detail || data.message || 'Errore nella creazione del socio';
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Create socio error:', error);
      // Distingui tra errore di rete e altri errori
      if (error.message === 'Failed to fetch') {
        toast.error('Impossibile connettersi al server. Verifica la connessione internet.');
      } else {
        toast.error(`Errore: ${error.message || 'Errore sconosciuto'}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSocio = async () => {
    // Validazione frontend
    if (formData.codice_fiscale && formData.codice_fiscale.length !== 16) {
      toast.error('Il Codice Fiscale deve essere di 16 caratteri');
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      // Clean up empty fields and _none values
      const cleanData = {};
      for (const [key, value] of Object.entries(formData)) {
        if (value && value !== '' && value !== '_none') {
          cleanData[key] = value;
        }
      }
      
      const response = await fetch(`${API_URL}/api/soci/${editingPersonaId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(cleanData)
      });

      // Clone response before reading to avoid "body stream already read" error
      const responseClone = response.clone();
      
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        // If JSON parsing fails, try to get text
        const text = await responseClone.text();
        console.error('JSON parse error:', parseError, 'Response text:', text);
        throw new Error('Risposta non valida dal server');
      }
      
      if (response.ok) {
        toast.success('Socio aggiornato con successo');
        if (data.volontario_created) {
          toast.success('Ruolo Volontario aggiunto! Completa la scheda dal modulo Volontari.', { duration: 5000 });
        } else if (data.volontario_error) {
          toast.warning(`Socio aggiornato, ma il ruolo Volontario NON è stato aggiunto: ${data.volontario_error}`, { duration: 6000 });
        }
        setShowEditDialog(false);
        setEditingPersonaId(null);
        setEditingHasVolontario(false);
        setFormData({...emptySocio});
        fetchSoci();
      } else {
        const errorMsg = data.detail || data.message || 'Errore nell\'aggiornamento';
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Edit socio error:', error);
      if (error.message === 'Failed to fetch') {
        toast.error('Impossibile connettersi al server. Verifica la connessione internet.');
      } else {
        toast.error(`Errore: ${error.message || 'Errore sconosciuto'}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // === BULK SELECTION HANDLERS (Idea Bonus) ===
  const toggleSelectSocio = (personaId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(personaId)) {
        next.delete(personaId);
      } else {
        next.add(personaId);
      }
      return next;
    });
  };

  const toggleSelectAllPage = () => {
    const pageSoci = soci.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
    const pageIds = pageSoci.map(s => s.persona_id);
    const allSelected = pageIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        pageIds.forEach(id => next.delete(id));
      } else {
        pageIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setBulkReport(null);
  };

  const handleBulkAddVolontario = async () => {
    if (selectedIds.size === 0) return;
    setBulkProcessing(true);
    setBulkReport(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/soci/bulk-add-volontario`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ persona_ids: Array.from(selectedIds) })
      });
      const data = await response.json();
      if (response.ok) {
        setBulkReport(data.report);
        const created = data.report?.created?.length || 0;
        const already = data.report?.already_volontario?.length || 0;
        const errors = data.report?.errors?.length || 0;
        if (created > 0) {
          toast.success(`${created} volontari creati con successo!`, { duration: 5000 });
        }
        if (already > 0) {
          toast.info(`${already} persone erano già Volontari (nessuna azione)`, { duration: 5000 });
        }
        if (errors > 0) {
          toast.warning(`${errors} errori — controlla il riepilogo`, { duration: 6000 });
        }
      } else {
        toast.error(data.detail || 'Errore durante l\'elaborazione in blocco');
      }
    } catch (error) {
      console.error('Bulk error:', error);
      toast.error(`Errore: ${error.message || 'Errore sconosciuto'}`);
    } finally {
      setBulkProcessing(false);
    }
  };

  const openEditDialog = async (socio) => {
    setEditingPersonaId(socio.persona_id);
    setEditingHasVolontario(false);
    setFormData({
      nome: socio.nome || '',
      cognome: socio.cognome || '',
      codice_fiscale: socio.codice_fiscale || '',
      email: socio.email || '',
      telefono: socio.telefono || '',
      cellulare: socio.cellulare || '',
      cellulare_secondario: socio.cellulare_secondario || '',
      data_nascita: socio.data_nascita || '',
      luogo_nascita: socio.luogo_nascita || '',
      sesso: socio.sesso || '',
      indirizzo: socio.indirizzo || '',
      via: socio.via || '',
      numero_civico: socio.numero_civico || '',
      citta: socio.citta || '',
      cap: socio.cap || '',
      provincia: socio.provincia || '',
      tipo_socio: socio.tipo_socio || 'ordinario',
      ruolo_associativo: socio.ruolo_associativo || '',
      tessera_associazione: socio.tessera_associazione || '',
      tessera_nazionale: socio.tessera_nazionale || '',
      familiare_collegato: socio.familiare_collegato || '',
      data_iscrizione_socio: socio.data_iscrizione_socio || '',
      note: socio.note || '',
      crea_anche_volontario: false
    });
    setShowEditDialog(true);
    
    // Recupera stato aggiornato del ruolo Volontario dal backend
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/soci/${socio.persona_id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setEditingHasVolontario(!!data.has_ruolo_volontario);
      }
    } catch (e) {
      console.error('Errore verifica ruolo volontario:', e);
    }
  };

  const openViewDialog = (socio) => {
    setViewingSocio(socio);
    setShowViewDialog(true);
  };

  const handleSospendi = async (personaId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/soci/${personaId}/sospendi`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Socio sospeso');
        fetchSoci();
        fetchStats();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const handleRiattiva = async (personaId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/soci/${personaId}/riattiva`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Socio riattivato');
        fetchSoci();
        fetchStats();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const handleCambiaStato = async (personaId, nuovoStato) => {
    const statoLabels = {
      cessato: 'Cessato',
      cessato_morosita: 'Cessato per Morosità',
      deceduto: 'Deceduto'
    };
    
    if (!window.confirm(`Confermi di voler impostare lo stato "${statoLabels[nuovoStato]}" per questo socio?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/soci/${personaId}/cambia-stato`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ nuovo_stato: nuovoStato })
      });

      if (response.ok) {
        toast.success(`Stato socio aggiornato: ${statoLabels[nuovoStato]}`);
        fetchSoci();
        fetchStats();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const handleEliminaSocio = async (personaId, nomeSocio) => {
    const conferma = window.confirm(
      `⚠️ ATTENZIONE: Stai per ELIMINARE DEFINITIVAMENTE il socio "${nomeSocio}".\n\n` +
      `Questa operazione è IRREVERSIBILE e rimuoverà:\n` +
      `- Tutti i dati anagrafici\n` +
      `- Tutti i ruoli associati\n` +
      `- Lo storico delle quote\n\n` +
      `Sei sicuro di voler procedere?`
    );
    
    if (!conferma) return;
    
    // Seconda conferma per sicurezza
    const doppiaConferma = window.confirm(
      `ULTIMA CONFERMA: Vuoi davvero eliminare definitivamente "${nomeSocio}"?`
    );
    
    if (!doppiaConferma) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/persone/${personaId}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(`Socio "${nomeSocio}" eliminato definitivamente`);
        fetchSoci();
        fetchStats();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Errore durante l\'eliminazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const getTipoSocioBadge = (tipo) => {
    const colors = {
      ordinario: 'bg-blue-100 text-blue-800',
      sostenitore: 'bg-green-100 text-green-800',
      onorario: 'bg-purple-100 text-purple-800',
      fondatore: 'bg-amber-100 text-amber-800'
    };
    return colors[tipo] || 'bg-gray-100 text-gray-800';
  };

  const getStatoBadge = (stato) => {
    const colors = {
      attivo: 'bg-green-100 text-green-800',
      sospeso: 'bg-yellow-100 text-yellow-800',
      cessato: 'bg-red-100 text-red-800',
      deceduto: 'bg-gray-100 text-gray-800',
      cessato_morosita: 'bg-orange-100 text-orange-800'
    };
    return colors[stato] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6" data-testid="soci-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="h-6 w-6" />
            Soci e Associati
          </h1>
          <p className="text-gray-500 mt-1">Gestione anagrafica soci dell'associazione</p>
        </div>
        <Button onClick={() => setShowNewDialog(true)} data-testid="new-socio-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nuovo Socio
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-600">{stats.soci?.attivi || 0}</div>
              <p className="text-sm text-gray-500">Soci Attivi</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-yellow-600">{stats.soci?.sospesi || 0}</div>
              <p className="text-sm text-gray-500">Soci Sospesi</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-blue-600">{stats.quote?.pagate || 0}/{stats.quote?.totale || 0}</div>
              <p className="text-sm text-gray-500">Quote Pagate {new Date().getFullYear()}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-emerald-600">€{stats.quote?.importo_incassato?.toFixed(2) || '0.00'}</div>
              <p className="text-sm text-gray-500">Incassato {new Date().getFullYear()}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Cerca per nome, cognome, CF, email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-soci"
                />
              </div>
            </div>
            <Select value={filtroTipo} onValueChange={setFiltroTipo}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Tipo Socio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutti i tipi</SelectItem>
                <SelectItem value="ordinario">Ordinario</SelectItem>
                <SelectItem value="sostenitore">Sostenitore</SelectItem>
                <SelectItem value="onorario">Onorario</SelectItem>
                <SelectItem value="fondatore">Fondatore</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filtroStato} onValueChange={setFiltroStato}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutti</SelectItem>
                <SelectItem value="attivo">Attivi</SelectItem>
                <SelectItem value="sospeso">Sospesi</SelectItem>
                <SelectItem value="cessato">Cessati</SelectItem>
                <SelectItem value="deceduto">Deceduti</SelectItem>
                <SelectItem value="cessato_morosita">Cessati per Morosità</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => fetchSoci()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Bulk actions toolbar - visibile solo con selezione attiva */}
      {selectedIds.size > 0 && (
        <Card className="border-primary bg-primary/5" data-testid="bulk-toolbar">
          <CardContent className="py-3">
            <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold">
                  {selectedIds.size}
                </div>
                <span className="text-sm font-medium">
                  {selectedIds.size === 1 ? 'socio selezionato' : 'soci selezionati'}
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearSelection}
                  data-testid="bulk-clear-selection-btn"
                >
                  Deseleziona tutto
                </Button>
                <Button
                  size="sm"
                  onClick={() => setShowBulkDialog(true)}
                  disabled={bulkProcessing}
                  data-testid="bulk-add-volontario-btn"
                >
                  <HandHelping className="h-4 w-4 mr-2" />
                  Promuovi anche a Volontario
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Caricamento...</div>
          ) : soci.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessun socio trovato</p>
              <Button variant="link" onClick={() => setShowNewDialog(true)}>
                Aggiungi il primo socio
              </Button>
            </div>
          ) : (
            <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">
                    <Checkbox
                      checked={
                        (() => {
                          const pageSoci = soci.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
                          if (pageSoci.length === 0) return false;
                          return pageSoci.every(s => selectedIds.has(s.persona_id));
                        })()
                      }
                      onCheckedChange={toggleSelectAllPage}
                      aria-label="Seleziona tutti"
                      data-testid="checkbox-select-all-page"
                    />
                  </TableHead>
                  <TableHead>Tessera</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead className="hidden md:table-cell">Email</TableHead>
                  <TableHead className="hidden md:table-cell">Tipo</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="hidden md:table-cell">Iscrizione</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {soci.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((socio) => (
                  <TableRow
                    key={socio.persona_id}
                    data-testid={`socio-row-${socio.persona_id}`}
                    data-selected={selectedIds.has(socio.persona_id) ? "true" : "false"}
                    className={selectedIds.has(socio.persona_id) ? "bg-primary/5" : ""}
                  >
                    <TableCell className="w-[40px]">
                      <Checkbox
                        checked={selectedIds.has(socio.persona_id)}
                        onCheckedChange={() => toggleSelectSocio(socio.persona_id)}
                        aria-label={`Seleziona ${socio.nome_completo}`}
                        data-testid={`checkbox-select-${socio.persona_id}`}
                      />
                    </TableCell>
                    <TableCell className="font-mono">{socio.numero_tessera || '-'}</TableCell>
                    <TableCell className="font-medium">
                      {socio.nome_completo || `${socio.cognome || ''} ${socio.nome || ''}`.trim() || '-'}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{socio.email || '-'}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTipoSocioBadge(socio.tipo_socio)}`}>
                        {socio.tipo_socio || 'ordinario'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatoBadge(socio.stato_socio)}`}>
                        {socio.stato_socio || 'attivo'}
                      </span>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{formatDateIT(socio.data_iscrizione) || '-'}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openViewDialog(socio)}>
                            <Eye className="h-4 w-4 mr-2" />
                            Visualizza
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEditDialog(socio)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Modifica
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => navigate(`/quote-associative?persona=${socio.persona_id}`)}>
                            <CreditCard className="h-4 w-4 mr-2" />
                            Quote
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {socio.stato_socio === 'attivo' && (
                            <DropdownMenuItem onClick={() => handleSospendi(socio.persona_id)} className="text-yellow-600">
                              <UserX className="h-4 w-4 mr-2" />
                              Sospendi
                            </DropdownMenuItem>
                          )}
                          {socio.stato_socio === 'sospeso' && (
                            <DropdownMenuItem onClick={() => handleRiattiva(socio.persona_id)} className="text-green-600">
                              <UserCheck className="h-4 w-4 mr-2" />
                              Riattiva
                            </DropdownMenuItem>
                          )}
                          {(socio.stato_socio === 'attivo' || socio.stato_socio === 'sospeso') && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleCambiaStato(socio.persona_id, 'cessato')} className="text-red-600">
                                <UserX className="h-4 w-4 mr-2" />
                                Cessa
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleCambiaStato(socio.persona_id, 'cessato_morosita')} className="text-orange-600">
                                <UserX className="h-4 w-4 mr-2" />
                                Cessa per Morosità
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleCambiaStato(socio.persona_id, 'deceduto')} className="text-gray-600">
                                <UserX className="h-4 w-4 mr-2" />
                                Deceduto
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleEliminaSocio(socio.persona_id, `${socio.cognome} ${socio.nome}`)} 
                            className="text-red-700 font-medium"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Elimina Definitivamente
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          {/* Paginazione */}
          {soci.length > ITEMS_PER_PAGE && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-sm text-slate-500">
                Pagina {currentPage} di {Math.ceil(soci.length / ITEMS_PER_PAGE)} — {soci.length} soci totali
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                  data-testid="prev-page-btn"
                >
                  Precedente
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={currentPage >= Math.ceil(soci.length / ITEMS_PER_PAGE)}
                  onClick={() => setCurrentPage(p => p + 1)}
                  data-testid="next-page-btn"
                >
                  Successiva
                </Button>
              </div>
            </div>
          )}
            </>
          )}
        </CardContent>
      </Card>

      {/* New/Edit Socio Dialog - Form Component */}
      <Dialog open={showNewDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowNewDialog(false);
          setShowEditDialog(false);
          setEditingPersonaId(null);
          setFormData({...emptySocio});
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{showEditDialog ? 'Modifica Socio' : 'Nuovo Socio'}</DialogTitle>
            <DialogDescription>
              {showEditDialog ? 'Modifica i dati del socio.' : 'Inserisci i dati del nuovo socio. Il numero tessera verrà generato automaticamente.'}
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="anagrafica" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="anagrafica">Anagrafica</TabsTrigger>
              <TabsTrigger value="contatti">Contatti</TabsTrigger>
              <TabsTrigger value="associazione">Associazione</TabsTrigger>
            </TabsList>
            
            {/* Tab Anagrafica */}
            <TabsContent value="anagrafica" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cognome">Cognome *</Label>
                  <Input
                    id="cognome"
                    value={formData.cognome}
                    onChange={(e) => setFormData({...formData, cognome: e.target.value})}
                    data-testid="input-cognome"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nome">Nome *</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({...formData, nome: e.target.value})}
                    data-testid="input-nome"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="cf">Codice Fiscale *</Label>
                <Input
                  id="cf"
                  value={formData.codice_fiscale}
                  onChange={(e) => setFormData({...formData, codice_fiscale: e.target.value.toUpperCase()})}
                  maxLength={16}
                  className="font-mono uppercase"
                  data-testid="input-cf"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="data_nascita">Data di Nascita</Label>
                  <Input
                    id="data_nascita"
                    type="date"
                    value={formData.data_nascita}
                    onChange={(e) => setFormData({...formData, data_nascita: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="luogo_nascita">Luogo di Nascita</Label>
                  <Input
                    id="luogo_nascita"
                    value={formData.luogo_nascita}
                    onChange={(e) => setFormData({...formData, luogo_nascita: e.target.value})}
                    placeholder="Città"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sesso">Sesso</Label>
                  <Select value={formData.sesso} onValueChange={(v) => setFormData({...formData, sesso: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">Maschio</SelectItem>
                      <SelectItem value="F">Femmina</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>
            
            {/* Tab Contatti */}
            <TabsContent value="contatti" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="mario.rossi@email.it"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cellulare">Cellulare</Label>
                  <Input
                    id="cellulare"
                    value={formData.cellulare}
                    onChange={(e) => setFormData({...formData, cellulare: e.target.value})}
                    placeholder="+39 333 1234567"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cellulare_secondario">Altro Cellulare</Label>
                  <Input
                    id="cellulare_secondario"
                    value={formData.cellulare_secondario}
                    onChange={(e) => setFormData({...formData, cellulare_secondario: e.target.value})}
                    placeholder="+39 340 9876543"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telefono">Telefono Fisso</Label>
                  <Input
                    id="telefono"
                    value={formData.telefono}
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                    placeholder="02 12345678"
                  />
                </div>
              </div>
              
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Indirizzo di Residenza
                </h4>
                <div className="space-y-4">
                  <div className="grid grid-cols-4 gap-4">
                    <div className="col-span-3 space-y-2">
                      <Label htmlFor="via">Via/Piazza</Label>
                      <Input
                        id="via"
                        value={formData.via}
                        onChange={(e) => setFormData({...formData, via: e.target.value})}
                        placeholder="Via Roma"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="numero_civico">N° Civico</Label>
                      <Input
                        id="numero_civico"
                        value={formData.numero_civico}
                        onChange={(e) => setFormData({...formData, numero_civico: e.target.value})}
                        placeholder="123"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="indirizzo">Indirizzo Completo (alternativo)</Label>
                    <Input
                      id="indirizzo"
                      value={formData.indirizzo}
                      onChange={(e) => setFormData({...formData, indirizzo: e.target.value})}
                      placeholder="Via Roma, 123 - Interno 4"
                      className="text-sm text-slate-600"
                    />
                    <p className="text-xs text-slate-400">Usa questo campo se l'indirizzo non rientra nei campi sopra</p>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="citta">Città</Label>
                      <Input
                        id="citta"
                        value={formData.citta}
                        onChange={(e) => setFormData({...formData, citta: e.target.value})}
                        placeholder="Milano"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cap">CAP</Label>
                      <Input
                        id="cap"
                        value={formData.cap}
                        onChange={(e) => setFormData({...formData, cap: e.target.value})}
                        maxLength={5}
                        placeholder="20100"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="provincia">Provincia</Label>
                      <Input
                        id="provincia"
                        value={formData.provincia}
                        onChange={(e) => setFormData({...formData, provincia: e.target.value.toUpperCase()})}
                        maxLength={2}
                        placeholder="MI"
                        className="uppercase"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
            
            {/* Tab Associazione */}
            <TabsContent value="associazione" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tipo_socio">Tipo Socio</Label>
                  <Select value={formData.tipo_socio} onValueChange={(v) => setFormData({...formData, tipo_socio: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ordinario">Ordinario</SelectItem>
                      <SelectItem value="sostenitore">Sostenitore</SelectItem>
                      <SelectItem value="onorario">Onorario</SelectItem>
                      <SelectItem value="fondatore">Fondatore</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ruolo_associativo">Ruolo nell'Associazione</Label>
                  <Select value={formData.ruolo_associativo} onValueChange={(v) => setFormData({...formData, ruolo_associativo: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona ruolo" />
                    </SelectTrigger>
                    <SelectContent>
                      {RUOLI_ASSOCIATIVI.map(r => (
                        <SelectItem key={r.value} value={r.value || '_none'}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="tessera_associazione">N° Tessera Associazione</Label>
                <Input
                  id="tessera_associazione"
                  value={formData.tessera_associazione}
                  onChange={(e) => setFormData({...formData, tessera_associazione: e.target.value})}
                  placeholder="Numero tessera rilasciato dall'ETS/APS/ODV"
                />
                <p className="text-xs text-muted-foreground">
                  Se l'associazione rilascia tessere proprie, inserisci qui il numero. Verrà usato nei documenti ufficiali.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="tessera_nazionale">N° Tessera Nazionale/Federazione</Label>
                <Input
                  id="tessera_nazionale"
                  value={formData.tessera_nazionale}
                  onChange={(e) => setFormData({...formData, tessera_nazionale: e.target.value})}
                  placeholder="Numero tessera federazione o ente nazionale"
                />
                <p className="text-xs text-muted-foreground">
                  Numero tessera rilasciato dalla federazione/ente nazionale di appartenenza (es. AVIS, ANPAS, etc.).
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="familiare_collegato">Familiare Collegato</Label>
                <Input
                  id="familiare_collegato"
                  value={formData.familiare_collegato}
                  onChange={(e) => setFormData({...formData, familiare_collegato: e.target.value})}
                  placeholder="Nome del familiare (es. Mario Rossi)"
                />
                <p className="text-xs text-muted-foreground">
                  Se il socio ha familiari iscritti, indica qui il nome per collegamento.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="data_iscrizione_socio">Socio dal</Label>
                <Input
                  id="data_iscrizione_socio"
                  type="date"
                  value={formData.data_iscrizione_socio}
                  onChange={(e) => setFormData({...formData, data_iscrizione_socio: e.target.value})}
                />
                <p className="text-xs text-muted-foreground">
                  Data di iscrizione come socio dell'associazione.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="note">Note</Label>
                <Textarea
                  id="note"
                  value={formData.note}
                  onChange={(e) => setFormData({...formData, note: e.target.value})}
                  placeholder="Note aggiuntive sul socio..."
                  rows={3}
                />
              </div>

              {/* Ruoli aggiuntivi - visibile sia in creazione che in modifica */}
              <div className="mt-4 p-4 border border-dashed border-primary/30 rounded-lg bg-primary/5 space-y-3">
                <div className="flex items-center gap-2">
                  <HandHelping className="h-4 w-4 text-primary" />
                  <Label className="font-semibold text-sm">Ruoli aggiuntivi della persona</Label>
                </div>
                <p className="text-xs text-muted-foreground">
                  Se questa persona ha anche altri ruoli, li aggiungiamo in un solo passaggio.
                  Potrai completare i dati specifici dalla sezione dedicata.
                </p>

                {/* Caso 1: in modifica e la persona è GIÀ Volontario → badge informativo */}
                {showEditDialog && editingHasVolontario ? (
                  <div className="flex items-center gap-2 pt-1 px-2 py-2 rounded-md bg-green-50 border border-green-200" data-testid="badge-gia-volontario">
                    <UserCheck className="h-4 w-4 text-green-600 shrink-0" />
                    <div className="grid gap-0.5 leading-none">
                      <span className="text-sm font-medium text-green-800">
                        Questa persona è già registrata come Volontario
                      </span>
                      <span className="text-xs text-green-700">
                        Puoi gestire la sua scheda dal modulo Volontari.
                      </span>
                    </div>
                  </div>
                ) : (
                  /* Caso 2: in creazione OPPURE in modifica e NON è ancora Volontario → checkbox */
                  <div className="flex items-start gap-2 pt-1">
                    <Checkbox
                      id="crea_anche_volontario"
                      checked={formData.crea_anche_volontario}
                      onCheckedChange={(checked) => setFormData({...formData, crea_anche_volontario: !!checked})}
                      data-testid="checkbox-crea-volontario"
                    />
                    <div className="grid gap-1 leading-none">
                      <Label
                        htmlFor="crea_anche_volontario"
                        className="text-sm font-medium cursor-pointer"
                      >
                        {showEditDialog ? 'Aggiungi anche il ruolo Volontario' : 'È anche un Volontario'}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Verrà creata automaticamente la scheda Volontario in stato &quot;Bozza&quot;.
                        Ricordati di completarla poi con documento identità e consenso privacy.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => {
              setShowNewDialog(false);
              setShowEditDialog(false);
              setEditingPersonaId(null);
              setFormData({...emptySocio});
            }}>
              Annulla
            </Button>
            <Button 
              onClick={showEditDialog ? handleEditSocio : handleCreateSocio} 
              disabled={submitting} 
              data-testid="save-socio-btn"
            >
              {submitting ? 'Salvataggio...' : (showEditDialog ? 'Salva Modifiche' : 'Crea Socio')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Socio Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Dettagli Socio</DialogTitle>
          </DialogHeader>
          {viewingSocio && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 pb-4 border-b">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{viewingSocio.nome_completo}</h3>
                  <p className="text-sm text-gray-500">Tessera: {viewingSocio.numero_tessera || 'N/A'}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Codice Fiscale</p>
                  <p className="font-mono">{viewingSocio.codice_fiscale}</p>
                </div>
                <div>
                  <p className="text-gray-500">Tipo Socio</p>
                  <p className="capitalize">{viewingSocio.tipo_socio}</p>
                </div>
                <div>
                  <p className="text-gray-500">Data Iscrizione</p>
                  <p>{formatDateIT(viewingSocio.data_iscrizione) || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Stato</p>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatoBadge(viewingSocio.stato_socio)}`}>
                    {viewingSocio.stato_socio}
                  </span>
                </div>
              </div>
              
              {(viewingSocio.email || viewingSocio.cellulare || viewingSocio.telefono || viewingSocio.cellulare_secondario) && (
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Phone className="h-4 w-4" /> Contatti
                  </h4>
                  <div className="space-y-1 text-sm">
                    {viewingSocio.email && <p><Mail className="inline h-3 w-3 mr-2" />{viewingSocio.email}</p>}
                    {viewingSocio.cellulare && <p><Phone className="inline h-3 w-3 mr-2" />{viewingSocio.cellulare}</p>}
                    {viewingSocio.cellulare_secondario && <p><Phone className="inline h-3 w-3 mr-2" />{viewingSocio.cellulare_secondario} (secondario)</p>}
                    {viewingSocio.telefono && <p><Phone className="inline h-3 w-3 mr-2" />{viewingSocio.telefono}</p>}
                  </div>
                </div>
              )}
              
              {(viewingSocio.tessera_nazionale || viewingSocio.familiare_collegato || viewingSocio.ruolo_associativo) && (
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Users className="h-4 w-4" /> Info Associative
                  </h4>
                  <div className="space-y-2 text-sm">
                    {viewingSocio.tessera_nazionale && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Tessera Nazionale:</span>
                        <span className="font-medium">{viewingSocio.tessera_nazionale}</span>
                      </div>
                    )}
                    {viewingSocio.ruolo_associativo && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Ruolo:</span>
                        <span className="font-medium capitalize">{viewingSocio.ruolo_associativo}</span>
                      </div>
                    )}
                    {viewingSocio.familiare_collegato && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Familiare:</span>
                        <span className="font-medium">{viewingSocio.familiare_collegato}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {(viewingSocio.indirizzo || viewingSocio.via || viewingSocio.citta) && (
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <MapPin className="h-4 w-4" /> Indirizzo
                  </h4>
                  <p className="text-sm">
                    {/* Concatena via + indirizzo + civico */}
                    {(viewingSocio.via || viewingSocio.indirizzo) && (
                      <>
                        {viewingSocio.via && viewingSocio.via !== 'VIA' && `${viewingSocio.via} `}
                        {viewingSocio.via === 'VIA' && 'VIA '}
                        {viewingSocio.indirizzo && `${viewingSocio.indirizzo}`}
                        {viewingSocio.numero_civico && `, ${viewingSocio.numero_civico}`}
                        <br />
                      </>
                    )}
                    {viewingSocio.cap} {viewingSocio.citta} {viewingSocio.provincia && `(${viewingSocio.provincia})`}
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>
              Chiudi
            </Button>
            <Button onClick={() => {
              setShowViewDialog(false);
              openEditDialog(viewingSocio);
            }}>
              <Edit className="h-4 w-4 mr-2" />
              Modifica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Add Volontario Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={(open) => {
        if (!open && !bulkProcessing) {
          setShowBulkDialog(false);
          if (bulkReport) {
            // Se c'è un report, ricarichiamo la lista e puliamo selezione
            clearSelection();
            fetchSoci();
          }
        }
      }}>
        <DialogContent className="max-w-lg" data-testid="bulk-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HandHelping className="h-5 w-5 text-primary" />
              Promuovi Soci a Volontari
            </DialogTitle>
            <DialogDescription>
              {!bulkReport
                ? `Stai per aggiungere il ruolo Volontario a ${selectedIds.size} ${selectedIds.size === 1 ? 'socio' : 'soci'}.`
                : 'Riepilogo dell\'operazione:'}
            </DialogDescription>
          </DialogHeader>

          {!bulkReport ? (
            <div className="space-y-3">
              <div className="rounded-md bg-muted/50 p-3 text-sm text-muted-foreground space-y-2">
                <p>
                  <strong>Cosa succederà:</strong>
                </p>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  <li>Per ogni socio selezionato verrà creata una scheda <strong>Volontario in stato &quot;Bozza&quot;</strong>.</li>
                  <li>Dati anagrafici (nome, cognome, CF, contatti) copiati automaticamente.</li>
                  <li>Soci che sono <strong>già Volontari</strong> saranno saltati (nessun duplicato).</li>
                  <li>Se raggiungi il <strong>limite del piano</strong>, i restanti saranno segnalati.</li>
                </ul>
                <p className="pt-1 text-xs">
                  ⚠️ Dovrai comunque completare ogni scheda Volontario con documento d&apos;identità e consenso privacy.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {/* Report */}
              <div className="grid grid-cols-3 gap-2">
                <div className="p-3 rounded-md bg-green-50 border border-green-200 text-center">
                  <div className="text-2xl font-bold text-green-700" data-testid="bulk-created-count">
                    {bulkReport.created?.length || 0}
                  </div>
                  <div className="text-xs text-green-800">Creati</div>
                </div>
                <div className="p-3 rounded-md bg-blue-50 border border-blue-200 text-center">
                  <div className="text-2xl font-bold text-blue-700" data-testid="bulk-already-count">
                    {bulkReport.already_volontario?.length || 0}
                  </div>
                  <div className="text-xs text-blue-800">Già Volontari</div>
                </div>
                <div className="p-3 rounded-md bg-orange-50 border border-orange-200 text-center">
                  <div className="text-2xl font-bold text-orange-700" data-testid="bulk-errors-count">
                    {bulkReport.errors?.length || 0}
                  </div>
                  <div className="text-xs text-orange-800">Errori</div>
                </div>
              </div>

              {/* Lista errori se presenti */}
              {bulkReport.errors && bulkReport.errors.length > 0 && (
                <div className="border rounded-md p-3 bg-orange-50/50">
                  <p className="text-xs font-semibold text-orange-900 mb-2">Errori riscontrati:</p>
                  <ul className="text-xs space-y-1 text-orange-800 max-h-[120px] overflow-y-auto">
                    {bulkReport.errors.slice(0, 20).map((err, idx) => (
                      <li key={idx}>
                        • {err.nome_completo || err.persona_id}: <em>{err.error}</em>
                      </li>
                    ))}
                    {bulkReport.errors.length > 20 && (
                      <li className="italic">...e altri {bulkReport.errors.length - 20} errori</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Lista già volontari */}
              {bulkReport.already_volontario && bulkReport.already_volontario.length > 0 && (
                <details className="border rounded-md p-3 bg-blue-50/50">
                  <summary className="text-xs font-semibold text-blue-900 cursor-pointer">
                    Vedi soci già Volontari ({bulkReport.already_volontario.length})
                  </summary>
                  <ul className="text-xs space-y-1 text-blue-800 mt-2 max-h-[120px] overflow-y-auto">
                    {bulkReport.already_volontario.map((v, idx) => (
                      <li key={idx}>• {v.nome_completo}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          <DialogFooter>
            {!bulkReport ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowBulkDialog(false)}
                  disabled={bulkProcessing}
                  data-testid="bulk-cancel-btn"
                >
                  Annulla
                </Button>
                <Button
                  onClick={handleBulkAddVolontario}
                  disabled={bulkProcessing}
                  data-testid="bulk-confirm-btn"
                >
                  {bulkProcessing ? 'Elaborazione...' : `Conferma (${selectedIds.size})`}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => {
                  setShowBulkDialog(false);
                  clearSelection();
                  fetchSoci();
                }}
                data-testid="bulk-close-btn"
              >
                Chiudi
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
